import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { PageHeader, SectionHeader, Kpi, Card, RangeSelect, centsToUsd } from "./adminUi";

export default function AdminLifeGuideAnalyticsPage() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    adminApi.lifeguideAnalytics({ range }).then(setData).catch(() => {});
  }, [range]);

  const g = data?.growth || {};
  const m = data?.membership || {};
  const e = data?.engagement || {};
  const gi = data?.gifts || {};
  const r = data?.retention || {};
  const gu = data?.guest || {};

  return (
    <div data-testid="admin-lifeguide-page" className="space-y-8">
      <PageHeader
        eyebrow="Product analytics"
        title="LifeGuide"
        subtitle="User growth, membership mix, product engagement, gifts, and retention."
        right={<RangeSelect value={range} onChange={setRange} testid="lg-range" />}
      />

      <section>
        <SectionHeader label="User growth" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="lg-total" label="Total users" value={g.total_users} />
          <Kpi testid="lg-new" label="New users (range)" value={g.new_users_in_range} />
          <Kpi testid="lg-active" label="Active users (range)" value={g.active_users_in_range} />
        </div>
        {g.series?.length > 0 && (
          <Card className="mt-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#8FA894] mb-3">
              New user signups over time
            </div>
            <MiniBarSeries data={g.series} valueKey="new_users" />
          </Card>
        )}
      </section>

      <section>
        <SectionHeader label="Membership" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="lg-free" label="Free" value={m.free} />
          <Kpi testid="lg-trial" label="Pro Trial" value={m.trial} />
          <Kpi testid="lg-paid" label="Paid Pro" value={m.paid_pro} tone="accent" />
          <Kpi testid="lg-gift" label="Gift" value={m.gift} />
          <Kpi testid="lg-conv" label="Trial → Paid" value={m.trial_to_paid_pct != null ? `${m.trial_to_paid_pct}%` : "—"} />
          <Kpi testid="lg-canc" label="Cancellations — 30d" value={m.cancellations_30d} />
        </div>
      </section>

      <section>
        <SectionHeader label="Engagement" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="lg-guidance-sessions" label="Guidance sessions" value={e.guidance_sessions} />
          <Kpi testid="lg-guidance-users" label="Guidance users" value={e.guidance_users} />
          <Kpi testid="lg-journal-entries" label="Journal entries" value={e.journal_entries} />
          <Kpi testid="lg-journal-users" label="Journal users" value={e.journal_users} />
          <Kpi testid="lg-wisdom-items" label="Saved wisdom items" value={e.saved_wisdom_items} />
          <Kpi testid="lg-wisdom-users" label="Saved wisdom users" value={e.saved_wisdom_users} />
          <Kpi testid="lg-weekly-refl" label="Weekly reflections" value={e.weekly_reflections} />
        </div>
      </section>

      <section>
        <SectionHeader label="Gifts" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="lg-gifts-purchased" label="Gifts purchased" value={gi.purchased} />
          <Kpi testid="lg-gifts-months" label="Months purchased" value={gi.months_purchased} />
          <Kpi testid="lg-gifts-redeemed" label="Gifts redeemed" value={gi.redeemed} />
          <Kpi testid="lg-gifts-redemption" label="Redemption %" value={gi.redemption_pct != null ? `${gi.redemption_pct}%` : "—"} />
          <Kpi testid="lg-gifts-anon" label="Anonymous gifts" value={gi.anonymous} />
          <Kpi testid="lg-gifts-named" label="Named gifts" value={gi.named} />
        </div>
      </section>

      <section>
        <SectionHeader label="Guest preview funnel" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="lg-guest-visitors" label="Guest visitors" value={gu.visitors} />
          <Kpi testid="lg-guest-from-proverb" label="From I Need a Proverb" value={gu.from_proverb} />
          <Kpi testid="lg-guest-interactions" label="Guest interactions" value={gu.interactions} />
          <Kpi testid="lg-guest-guidance" label="Guest guidance" value={gu.guidance_interactions} />
          <Kpi testid="lg-guest-coach" label="Guest coach" value={gu.coach_interactions} />
          <Kpi testid="lg-guest-limit" label="Hit the free cap" value={gu.limit_reached} />
          <Kpi
            testid="lg-guest-conv"
            label="Guest → account"
            value={gu.converted_to_account}
            tone="accent"
          />
          <Kpi
            testid="lg-guest-conv-pct"
            label="Conversion %"
            value={gu.conversion_pct != null ? `${gu.conversion_pct}%` : "—"}
          />
        </div>
      </section>

      <section>
        <SectionHeader label="Retention (rolling)" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="lg-ret-7" label="Active — 7 days" value={r.active_7d} />
          <Kpi testid="lg-ret-30" label="Active — 30 days" value={r.active_30d} />
          <Kpi testid="lg-ret-90" label="Active — 90 days" value={r.active_90d} />
        </div>
      </section>
    </div>
  );
}

function MiniBarSeries({ data, valueKey }) {
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  return (
    <div className="flex items-end gap-1.5 h-28" data-testid="lg-growth-series">
      {data.map((d) => (
        <div key={d.bucket} className="flex-1 flex flex-col items-center justify-end">
          <div
            title={`${d.bucket}: ${d[valueKey]}`}
            className="w-full rounded-t-md bg-[#4F6F52]/70"
            style={{ height: `${Math.max(2, (d[valueKey] / max) * 100)}%` }}
          />
          <div className="text-[9px] text-[#607467] mt-1">{d.bucket.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}
